import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Alert, Autocomplete, Button, Flex, FormField, Grid, Type } from "mesh-component-library";
import { uniqueId } from "lodash";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { StateKeys, StateList } from "../../utilities/StateList";
import { LicensePlateData } from "../../reducers/reducer-license-plate-vin-lookup";
import { generatePath } from "../../utilities/RedirectUtils";
import { VEHICLE_NOT_FOUND_VIN } from "../../app-consts/errorMessageConstants";
import { VINResponse } from "../../screens/AddVehicle/AddVehicleByLicensePlateVin/AddVehicleTypes";

export type LicensePlateLookupProps = {
  changeLookupTypeMethod: (lookupType: "VIN") => void;
  resetSearchResults: () => void;
  resetVinSearchResult: () => void;
  searchByLicensePlate: (data: LicensePlateData) => void;
  setLicensePlate: (data: LicensePlateData) => void;
  vinSearchResult: VINResponse;
};

export const LicensePlateLookup = ({
  changeLookupTypeMethod,
  resetSearchResults,
  resetVinSearchResult,
  searchByLicensePlate,
  setLicensePlate,
  vinSearchResult,
}: LicensePlateLookupProps) => {
  const [plateNumber, setPlateNumber] = useState<string>("");
  const [licenseError, setLicenseError] = useState<boolean>(false);
  const [state, setState] = useState<StateKeys | "">("");
  const [stateError, setStateError] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const id = uniqueId();
  const getTranslatedText = useLocale();
  const history = useHistory();

  useEffect(() => {
    const debounce = setTimeout(() => {
      const hasInteractedWithPlate = plateNumber !== "";
      const hasInteractedWithState = state !== "";
      const isLicensePlateValid = plateNumber.length >= 6 && plateNumber.length <= 9;
      const isStateValid = state.length === 2;

      setLicenseError(hasInteractedWithPlate && !isLicensePlateValid);
      setStateError(hasInteractedWithState && !isStateValid);

      setButtonDisabled(!isLicensePlateValid || !isStateValid);
    }, 500);

    return () => clearTimeout(debounce);
  });

  const onConfirmVehicle = () => {
    searchByLicensePlate({
      licensePlate: plateNumber,
      state: state as StateKeys,
    });
    setLicensePlate({
      licensePlate: plateNumber,
      state: state as StateKeys,
    });
    history.push(generatePath("vehicle-search"));
  };

  const onLicensePlateChange = ({ target: { value } }) => {
    setPlateNumber(value);
  };

  const onStateChange = (event, selectedState) => {
    if (selectedState?.selectedItem) {
      setState(selectedState?.selectedItem.value);
      setStateError(false);
    } else {
      setState("");
      setStateError(true);
    }
  };

  const renderLicensePlateLabel = () => (
    <Flex justifyContent="between" isBlock={true}>
      <Type size="lg" weight="bold" emphasis="information">
        {getTranslatedText(TRANSLATION_CONSTANTS.LICENSE_PLATE_NUMBER)}
      </Type>
    </Flex>
  );

  const renderStateLabel = () => (
    <Type size="lg" weight="bold" emphasis="information">
      {getTranslatedText(TRANSLATION_CONSTANTS.STATE)}
    </Type>
  );

  const validateLicensePlateNumber = (): boolean => {
    const isValid = plateNumber && plateNumber.length >= 6 && plateNumber.length <= 9;

    if (isValid) {
      setLicenseError(false);
    } else {
      setLicenseError(true);
    }

    return isValid;
  };

  const handleChangeToVinSearch = () => {
    changeLookupTypeMethod("VIN");
    resetSearchResults();
    resetVinSearchResult();
  };

  return (
    <>
      <Grid
        utils={{
          vrBottom: "md",
        }}
      >
        {vinSearchResult?.error && <Alert title={VEHICLE_NOT_FOUND_VIN} variant="critical" />}
        <Grid.Column>
          <Autocomplete
            id={`license-plate-states-autocomplete-${id}`}
            data-testid="LP-lookup-state-autocomplete"
            name="State"
            label={renderStateLabel()}
            hasError={stateError}
            errorText={getTranslatedText(TRANSLATION_CONSTANTS.SELECT_A_STATE)}
            placeholder={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_STATE)}
            value={state}
            suggestions={[...StateList]}
            clearBtn={true}
            onChange={(first, second) => {
              onStateChange(first, second);
            }}
            required
          />
        </Grid.Column>
        <Grid.Column>
          <FormField
            size="md"
            id={`license-plate-number-${id}`}
            name="License Plate Number"
            label={renderLicensePlateLabel()}
            value={plateNumber}
            data-testid={"license-plate-lookup-input"}
            utils={{
              vrTop: "xs",
              vrBottom: "xs",
            }}
            hasError={licenseError}
            errorText={getTranslatedText(TRANSLATION_CONSTANTS.INVALID_LICENSE_PLATE_NUMBER)}
            maxLength={9}
            onBlur={validateLicensePlateNumber}
            onChange={onLicensePlateChange}
            regex={/[^a-zA-Z0-9-]+/}
            required
          />
        </Grid.Column>
        <Grid.Column
          utils={{
            vrBottom: "md",
          }}
        >
          <Button
            hasUpgrade
            disabled={buttonDisabled}
            utils={{
              fullWidth: true,
            }}
            onClick={onConfirmVehicle}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE)}
          </Button>
        </Grid.Column>
        <Grid.Column>
          <Type element="span" size="4xl" variant="darker" weight="normal">
            - {getTranslatedText(TRANSLATION_CONSTANTS.OR)} -
          </Type>
        </Grid.Column>
        <Grid.Column>
          <Type
            data-testid="LP-search-by-VIN"
            element="span"
            size="lg"
            variant="information"
            weight="semibold"
            onClick={handleChangeToVinSearch}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.SEARCH_BY_VIN)}
          </Type>
        </Grid.Column>
      </Grid>
    </>
  );
};
